import React, { useEffect, useState } from 'react';
import { CheckCircle2, Download, Trash2, X, ZoomIn } from 'lucide-react';

const PREVIEW_FRAME =
  'aspect-[4/3] w-full flex items-center justify-center bg-muted/40';

/**
 * Emplacement photo : aperçu / guide, puis « Choisir » / « Remplacer » et « Supprimer ».
 */
export default function PhotoSlotUploadCard({
  item,
  inputId,
  pendingFile,
  previewUrl,
  uploadedPreviewUrl,
  uploadedRecord,
  isUploading = false,
  disabled = false,
  onFileChange,
  onRemove,
  onDownload,
}) {
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const hasPending = Boolean(pendingFile);
  const hasUploaded = Boolean(uploadedRecord) && !hasPending;
  const hasImage = hasPending || hasUploaded;
  const isComplete = hasUploaded;
  const displayName = pendingFile?.name || uploadedRecord?.name || null;
  const imageSrc =
    (hasPending && previewUrl) || uploadedPreviewUrl || null;
  const previewLoading = hasUploaded && !imageSrc;
  const canOpenLightbox = Boolean(imageSrc);

  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setLightboxOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [lightboxOpen]);

  return (
    <div className="rounded-xl border border-border bg-card p-3 flex flex-col gap-3 min-w-0 h-full">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-foreground">{item.label}</p>
        <span
          className={`text-[10px] font-semibold uppercase tracking-wide shrink-0 ${
            isComplete ? 'text-success' : 'text-destructive'
          }`}
        >
          {isComplete ? 'OK' : 'Obligatoire'}
        </span>
      </div>

      <div
        className={`relative rounded-lg border overflow-hidden ${
          hasImage ? 'border-success/50' : 'border-border'
        }`}
      >
        {imageSrc ? (
          <button
            type="button"
            onClick={() => setLightboxOpen(true)}
            className={`${PREVIEW_FRAME} group cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2`}
            aria-label={`Agrandir : ${item.label}`}
          >
            <img
              src={imageSrc}
              alt={`Aperçu : ${item.label}`}
              className="max-h-full max-w-full h-full w-full object-contain p-1"
              loading="lazy"
            />
            <span className="pointer-events-none absolute inset-0 flex items-end justify-end p-2 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
              <span className="rounded-md bg-background/90 p-1.5 text-foreground shadow-sm border border-border">
                <ZoomIn className="w-4 h-4" aria-hidden />
              </span>
            </span>
          </button>
        ) : previewLoading ? (
          <div className={`${PREVIEW_FRAME} text-xs text-muted-foreground`}>
            Chargement de l&apos;aperçu…
          </div>
        ) : (
          <div className={PREVIEW_FRAME}>
            <img
              src={item.guideSrc}
              alt={item.guideAlt}
              className="max-h-full max-w-full h-full w-full object-contain p-2 opacity-95"
              loading="lazy"
            />
          </div>
        )}
        {hasImage ? (
          <div className="absolute top-2 right-2 pointer-events-none">
            <CheckCircle2
              className="w-5 h-5 text-success drop-shadow-sm"
              strokeWidth={2.5}
              aria-hidden
            />
          </div>
        ) : null}
      </div>

      {displayName ? (
        <p
          className="text-xs font-semibold text-foreground text-center truncate px-1"
          title={displayName}
        >
          {displayName}
        </p>
      ) : null}

      <div className="flex flex-col gap-2 mt-auto">
        <input
          id={inputId}
          type="file"
          accept="image/*"
          className="sr-only"
          disabled={disabled || isUploading}
          onChange={onFileChange}
        />
        <label
          htmlFor={inputId}
          className={`flex w-full min-h-[44px] items-center justify-center rounded-lg border text-sm font-semibold transition-colors ${
            disabled || isUploading
              ? 'border-border bg-muted/40 text-muted-foreground cursor-not-allowed'
              : 'border-primary/40 bg-primary/10 text-primary hover:bg-primary/15 cursor-pointer'
          }`}
        >
          {hasImage ? 'Remplacer image' : 'Choisir une image'}
        </label>

        {hasUploaded && (onDownload || onRemove) ? (
          <div
            className={`grid gap-2 ${onDownload && onRemove ? 'grid-cols-2' : 'grid-cols-1'}`}
          >
            {onDownload ? (
              <button
                type="button"
                onClick={onDownload}
                disabled={isUploading}
                className="flex min-h-[44px] items-center justify-center gap-2 rounded-lg border border-border bg-background text-sm font-medium text-foreground hover:bg-muted/50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
              >
                <Download className="w-4 h-4 shrink-0" aria-hidden />
                Télécharger
              </button>
            ) : null}
            {onRemove ? (
              <button
                type="button"
                onClick={onRemove}
                disabled={disabled || isUploading}
                className="flex min-h-[44px] items-center justify-center gap-2 rounded-lg border border-destructive/35 bg-destructive/5 text-sm font-medium text-destructive hover:bg-destructive/10 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
              >
                <Trash2 className="w-4 h-4 shrink-0" aria-hidden />
                Supprimer
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      {lightboxOpen && canOpenLightbox ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setLightboxOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label={item.label}
        >
          <div
            className="relative flex max-h-[90vh] max-w-5xl flex-col overflow-hidden rounded-lg bg-card shadow-lg"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
              <h3 className="truncate text-base font-semibold text-foreground">{item.label}</h3>
              <button
                type="button"
                onClick={() => setLightboxOpen(false)}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors cursor-pointer"
                aria-label="Fermer l’aperçu"
              >
                <X className="w-5 h-5" aria-hidden />
              </button>
            </div>
            <div className="flex min-h-0 flex-1 items-center justify-center bg-muted/30 p-4">
              <img
                src={imageSrc}
                alt={item.label}
                className="max-h-[75vh] max-w-full object-contain"
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
